import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

/* ── JepTags Logo ──────────────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="contact-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" />
        <stop offset="50%" stopColor="#00E5CC" />
        <stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#contact-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#contact-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#contact-ng)">J</text>
  </svg>
);

/* ── Icons ─────────────────────────────────────────────────── */
const Ico = {
  arrowLeft: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" /></svg>,
  phone: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>,
  mapPin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12l5 5L19 7" /></svg>,
  msgCircle: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>,
  headphones: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 18v-6a9 9 0 0118 0v6" /><path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" /></svg>,
};

const ADMIN_EMAIL = "jeptags@gmail.com";

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "General Inquiry", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);

    // Build mailto link with form data
    const subject = encodeURIComponent(`[JepTags Contact] ${form.subject} — ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || "N/A"}\nCompany: ${form.company || "N/A"}\nSubject: ${form.subject}\n\n--- Message ---\n${form.message}\n\n---\nSent from JepTags Contact Form`
    );

    // Open mail client
    window.location.href = `mailto:${ADMIN_EMAIL}?subject=${subject}&body=${body}`;

    // Show success after a brief delay
    setTimeout(() => {
      setSending(false);
      setSent(true);
    }, 1000);
  };

  return (
    <div className="ct-root">
      <style dangerouslySetInnerHTML={{ __html: CT_STYLES }} />

      {/* NAV */}
      <nav className="ct-nav">
        <a href="#" className="ct-nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={36} />
          <span className="ct-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
        </a>
        <button className="ct-back" onClick={() => navigate(-1)}>
          <span className="ct-ico-16">{Ico.arrowLeft}</span> Back
        </button>
      </nav>

      {/* HERO */}
      <section className="ct-hero">
        <div className="ct-badge">Contact Us</div>
        <h1>Let's <span className="grad">Talk</span></h1>
        <p>Have questions about JepTags? Want to protect your brand? Our team is ready to help you get started.</p>
      </section>

      {/* MAIN CONTENT */}
      <section className="ct-main">
        <div className="ct-grid">

          {/* LEFT — Contact Info */}
          <div className="ct-info">
            <h2>Get in Touch</h2>
            <p className="ct-info-sub">Reach out through any of these channels, or fill out the form and we'll get back to you within 24 hours.</p>

            <div className="ct-info-cards">
              <div className="ct-icard">
                <div className="ct-icard-ico">{Ico.mail}</div>
                <div>
                  <div className="ct-icard-title">Email</div>
                  <a href={`mailto:${ADMIN_EMAIL}`} className="ct-icard-val">{ADMIN_EMAIL}</a>
                </div>
              </div>

              <div className="ct-icard">
                <div className="ct-icard-ico">{Ico.phone}</div>
                <div>
                  <div className="ct-icard-title">Phone (USA)</div>
                  <a href="tel:+18447374799" className="ct-icard-val">+1-844-737-4799</a>
                </div>
              </div>

              <div className="ct-icard">
                <div className="ct-icard-ico">{Ico.phone}</div>
                <div>
                  <div className="ct-icard-title">Phone (Asia)</div>
                  <a href="tel:+84938681686" className="ct-icard-val">+84-938-681-686</a>
                </div>
              </div>

              <div className="ct-icard">
                <div className="ct-icard-ico">{Ico.mapPin}</div>
                <div>
                  <div className="ct-icard-title">USA Office</div>
                  <div className="ct-icard-val">2635 Cunningham Ave, CA 95148</div>
                </div>
              </div>

              <div className="ct-icard">
                <div className="ct-icard-ico">{Ico.mapPin}</div>
                <div>
                  <div className="ct-icard-title">Vietnam Office</div>
                  <div className="ct-icard-val">15 Dang Thuy Tram, Hanoi</div>
                </div>
              </div>

              <div className="ct-icard">
                <div className="ct-icard-ico">{Ico.clock}</div>
                <div>
                  <div className="ct-icard-title">Business Hours</div>
                  <div className="ct-icard-val">Mon–Fri, 9AM–6PM (PST)</div>
                </div>
              </div>
            </div>

            {/* Quick Help */}
            <div className="ct-quick">
              <h3>Quick Help</h3>
              <div className="ct-quick-links">
                <a href="#" onClick={e => { e.preventDefault(); navigate('/faq'); }}>
                  <span className="ct-ico-18">{Ico.msgCircle}</span> FAQ — Common Questions
                </a>
                <a href="#" onClick={e => { e.preventDefault(); navigate('/about'); }}>
                  <span className="ct-ico-18">{Ico.headphones}</span> About JepTags
                </a>
                <a href="#" onClick={e => { e.preventDefault(); navigate('/business/register'); }}>
                  <span className="ct-ico-18">{Ico.send}</span> Start Free Trial
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className="ct-form-wrap">
            {sent ? (
              <div className="ct-success">
                <div className="ct-success-ico">{Ico.check}</div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. Your email client should have opened with the message. Our team will respond within 24 hours.</p>
                <button className="btn-p" onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", company: "", subject: "General Inquiry", message: "" }); }}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <>
                <h2>Send Us a Message</h2>
                <p className="ct-form-sub">Fill out the form below and we'll get back to you shortly.</p>

                <div className="ct-form">
                  <div className="ct-row">
                    <div className="ct-field">
                      <label>Full Name <span className="ct-req">*</span></label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" required />
                    </div>
                    <div className="ct-field">
                      <label>Email <span className="ct-req">*</span></label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" required />
                    </div>
                  </div>
                  <div className="ct-row">
                    <div className="ct-field">
                      <label>Phone</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 123-4567" />
                    </div>
                    <div className="ct-field">
                      <label>Company</label>
                      <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Your company name" />
                    </div>
                  </div>
                  <div className="ct-field">
                    <label>Subject <span className="ct-req">*</span></label>
                    <select name="subject" value={form.subject} onChange={handleChange}>
                      <option>General Inquiry</option>
                      <option>Product Authentication</option>
                      <option>Pricing & Plans</option>
                      <option>Partnership & Integration</option>
                      <option>Technical Support</option>
                      <option>Schedule a Demo</option>
                      <option>Investor Relations</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="ct-field">
                    <label>Message <span className="ct-req">*</span></label>
                    <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us how we can help you..." rows={5} required />
                  </div>
                  <button
                    type="button"
                    className={`btn-p ct-submit ${sending ? 'sending' : ''}`}
                    disabled={!form.name || !form.email || !form.message || sending}
                    onClick={handleSubmit}
                  >
                    {sending ? (
                      <><span className="ct-spinner" /> Sending...</>
                    ) : (
                      <><span className="ct-ico-16">{Ico.send}</span> Send Message</>
                    )}
                  </button>
                  <p className="ct-privacy-note">By submitting, you agree to our <a href="#" onClick={e => { e.preventDefault(); navigate('/privacy'); }}>Privacy Policy</a>.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CSS — SAME dark navy / cyan / teal theme
   ══════════════════════════════════════════════════════════════ */
const CT_STYLES = `
.ct-root *,.ct-root *::before,.ct-root *::after{margin:0;padding:0;box-sizing:border-box}
.ct-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;min-height:100vh;
}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.ct-ico-16 svg{width:16px;height:16px}
.ct-ico-18 svg{width:18px;height:18px}

/* NAV */
.ct-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:16px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.85);border-bottom:1px solid rgba(0,229,204,0.06)}
.ct-nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;cursor:pointer}
.ct-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.ct-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.ct-back:hover{border-color:var(--cyan);color:var(--cyan)}

/* BUTTONS */
.btn-p{padding:14px 28px;border:none;border-radius:12px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-family:'Sora',sans-serif;font-size:15px;font-weight:700;cursor:pointer;transition:transform .3s,box-shadow .3s;display:inline-flex;align-items:center;gap:8px;text-decoration:none}
.btn-p:hover{transform:translateY(-2px);box-shadow:0 12px 40px rgba(0,229,204,0.35)}
.btn-p:disabled{opacity:0.4;cursor:not-allowed;transform:none;box-shadow:none}

/* HERO */
.ct-hero{padding:140px 40px 60px;text-align:center;max-width:680px;margin:0 auto}
.ct-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.ct-hero h1{font-size:clamp(36px,5vw,56px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:16px}
.ct-hero p{font-size:17px;line-height:1.7;color:rgba(224,240,240,0.5);max-width:560px;margin:0 auto}

/* MAIN */
.ct-main{max-width:1100px;margin:0 auto;padding:20px 40px 100px}
.ct-grid{display:grid;grid-template-columns:1fr 1.2fr;gap:48px;align-items:start}

/* LEFT — INFO */
.ct-info h2{font-size:24px;font-weight:800;margin-bottom:8px}
.ct-info-sub{font-size:14px;color:rgba(224,240,240,0.45);line-height:1.6;margin-bottom:28px}

.ct-info-cards{display:flex;flex-direction:column;gap:14px;margin-bottom:32px}
.ct-icard{display:flex;align-items:flex-start;gap:14px;padding:16px;border-radius:14px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);transition:border-color .3s}
.ct-icard:hover{border-color:rgba(0,229,204,0.15)}
.ct-icard-ico{width:36px;height:36px;border-radius:10px;background:rgba(0,229,204,0.08);color:var(--cyan);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ct-icard-ico svg{width:18px;height:18px}
.ct-icard-title{font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:rgba(224,240,240,0.35);margin-bottom:4px}
.ct-icard-val{font-size:14px;color:rgba(224,240,240,0.7)}
a.ct-icard-val{text-decoration:none;transition:color .3s}
a.ct-icard-val:hover{color:var(--cyan)}

.ct-quick{padding:24px;border-radius:16px;background:rgba(0,229,204,0.04);border:1px solid rgba(0,229,204,0.08)}
.ct-quick h3{font-size:14px;font-weight:700;margin-bottom:14px}
.ct-quick-links{display:flex;flex-direction:column;gap:10px}
.ct-quick-links a{display:flex;align-items:center;gap:10px;font-size:13px;color:rgba(224,240,240,0.5);text-decoration:none;transition:color .3s;padding:8px 12px;border-radius:8px;transition:all .3s}
.ct-quick-links a:hover{color:var(--cyan);background:rgba(0,229,204,0.06)}

/* RIGHT — FORM */
.ct-form-wrap{padding:40px;border-radius:24px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.08)}
.ct-form-wrap h2{font-size:24px;font-weight:800;margin-bottom:6px}
.ct-form-sub{font-size:14px;color:rgba(224,240,240,0.45);margin-bottom:24px}

.ct-form{display:flex;flex-direction:column;gap:18px}
.ct-row{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.ct-field{display:flex;flex-direction:column;gap:6px}
.ct-field label{font-size:12px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:rgba(224,240,240,0.45)}
.ct-req{color:var(--cyan)}
.ct-field input,.ct-field select,.ct-field textarea{
  width:100%;padding:14px 16px;border-radius:12px;border:1.5px solid rgba(0,229,204,0.1);
  background:rgba(3,10,20,0.6);color:#e0f0f0;font-family:'Sora',sans-serif;font-size:14px;
  transition:all .3s;outline:none;resize:vertical;
}
.ct-field input::placeholder,.ct-field textarea::placeholder{color:rgba(224,240,240,0.2)}
.ct-field input:focus,.ct-field select:focus,.ct-field textarea:focus{border-color:var(--cyan);box-shadow:0 0 0 3px rgba(0,229,204,0.1)}
.ct-field select{appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2300E5CC' stroke-width='2'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center;cursor:pointer}
.ct-field select option{background:var(--navy);color:#e0f0f0}

.ct-submit{width:100%;justify-content:center;padding:16px;font-size:16px;margin-top:4px}
.ct-spinner{width:18px;height:18px;border:2.5px solid rgba(3,10,20,0.2);border-top-color:var(--navy-deep);border-radius:50%;animation:ctSpin .6s linear infinite;display:inline-block}
@keyframes ctSpin{to{transform:rotate(360deg)}}

.ct-privacy-note{font-size:11px;color:rgba(224,240,240,0.25);text-align:center;margin-top:4px}
.ct-privacy-note a{color:var(--cyan);text-decoration:none}
.ct-privacy-note a:hover{text-decoration:underline}

/* SUCCESS */
.ct-success{text-align:center;padding:48px 24px}
.ct-success-ico{width:64px;height:64px;border-radius:50%;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));display:flex;align-items:center;justify-content:center;margin:0 auto 20px}
.ct-success-ico svg{width:32px;height:32px;color:var(--navy-deep)}
.ct-success h3{font-size:24px;font-weight:800;margin-bottom:10px}
.ct-success p{font-size:14px;color:rgba(224,240,240,0.5);line-height:1.6;margin-bottom:24px;max-width:400px;margin-left:auto;margin-right:auto}

/* RESPONSIVE */
@media(max-width:900px){
  .ct-nav{padding:14px 20px}
  .ct-hero{padding:120px 24px 40px}
  .ct-main{padding:20px 24px 80px}
  .ct-grid{grid-template-columns:1fr;gap:32px}
  .ct-form-wrap{padding:28px}
}
@media(max-width:600px){
  .ct-row{grid-template-columns:1fr}
}
`;
